/**
 * Alert sound utility for sales alerts and other notifications.
 * Uses Web Audio API to generate a short beep pattern without needing an audio file.
 * Also shows a browser notification if permission was granted.
 *
 * The user gesture requirement means the first call may fail silently; we
 * resume the audio context on the first user interaction so subsequent alerts work.
 *
 * Global mute (LS_MUTE_KEY): when true, playAlertSound() becomes a no-op.
 * Used by the header mute toggle in App.vue.
 */

const LS_MUTE_KEY = 'notifications_muted'

let _ctx = null
let _resumeBound = false

/** Returns true if the user has globally muted notification sounds. */
export function isNotificationsMuted() {
  try {
    return localStorage.getItem(LS_MUTE_KEY) === 'true'
  } catch {
    return false
  }
}

/** Persist mute flag. Pass a boolean. Returns the new value. */
export function setNotificationsMuted(val) {
  try {
    localStorage.setItem(LS_MUTE_KEY, val ? 'true' : 'false')
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent('notifications-mute-changed', { detail: !!val }))
  } catch {}
  return !!val
}

/** Convenience: flip the mute flag. Returns new value. */
export function toggleNotificationsMuted() {
  return setNotificationsMuted(!isNotificationsMuted())
}

function _getCtx() {
  if (!_ctx) {
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext
      if (!Ctor) return null
      _ctx = new Ctor()
    } catch (e) {
      return null
    }
  }
  if (_ctx.state === 'suspended' && !_resumeBound) {
    _resumeBound = true
    const resume = () => { _ctx.resume().catch(() => {}) }
    window.addEventListener('click', resume, { once: true })
    window.addEventListener('keydown', resume, { once: true })
    window.addEventListener('touchstart', resume, { once: true })
  }
  return _ctx
}

/**
 * Play a single soft bell chime.
 *
 * One short, pleasant "ding" — deliberately NOT a multi-tone urgent beep.
 * Users consistently asked for a less aggressive sound, so severity no longer
 * changes the pattern (kept as an argument for backwards compatibility); only
 * the overall volume is slightly higher for `critical`.
 *
 * Implementation:
 *   Bell-like timbre from one fundamental + one high partial, with a sharp
 *   attack and an exponential decay envelope. No repeats.
 */
export function playAlertSound(severity = 'warning') {
  if (isNotificationsMuted()) return
  const ctx = _getCtx()
  if (!ctx) return
  try {
    const now = ctx.currentTime
    const baseVol = severity === 'critical' ? 0.16 : 0.12
    const fundamental = 880   // A5 — soft, non-urgent
    const partial     = 1760  // octave above, adds "bell" shimmer
    const dur         = 0.9

    // Two oscillators into one gain envelope — a plucked / bell-like tone.
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(baseVol, now + 0.005)  // sharp attack
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur) // long exp decay

    const osc1 = ctx.createOscillator()
    osc1.type = 'sine'
    osc1.frequency.value = fundamental

    const osc2 = ctx.createOscillator()
    osc2.type = 'sine'
    osc2.frequency.value = partial
    const partialGain = ctx.createGain()
    partialGain.gain.value = 0.35   // quieter partial

    osc1.connect(gain)
    osc2.connect(partialGain).connect(gain)
    gain.connect(ctx.destination)

    osc1.start(now)
    osc2.start(now)
    osc1.stop(now + dur + 0.05)
    osc2.stop(now + dur + 0.05)
  } catch (e) {
    console.warn('[alertSound] playAlertSound failed', e)
  }
}

/**
 * Show a browser Notification (if permission granted). Silently noop otherwise.
 */
export function showDesktopNotification(title, body, opts = {}) {
  try {
    if (!('Notification' in window)) return
    if (Notification.permission !== 'granted') return
    const n = new Notification(title || 'Alert', {
      body: body || '',
      icon: opts.icon || '/favicon.ico',
      tag: opts.tag || 'sales-alert',
      silent: !!opts.silent,
    })
    if (opts.onClick) n.onclick = opts.onClick
  } catch (e) {
    console.warn('[alertSound] desktop notification failed', e)
  }
}

/**
 * Request notification permission; safe to call multiple times.
 * Returns a promise resolving to the current permission string.
 */
export function requestNotificationPermission() {
  if (!('Notification' in window)) return Promise.resolve('unsupported')
  if (Notification.permission === 'granted') return Promise.resolve('granted')
  if (Notification.permission === 'denied') return Promise.resolve('denied')
  try {
    return Notification.requestPermission()
  } catch {
    return Promise.resolve('default')
  }
}

/**
 * Helper: is this notification a "sales alert" that should trigger sound?
 * We look for notification_type prefix 'sales_alert_' or 'sales_supervisor_'.
 */
export function isSalesAlert(notif) {
  if (!notif || !notif.notification_type) return false
  const t = String(notif.notification_type)
  return t.startsWith('sales_alert_') || t.startsWith('sales_supervisor_')
}
